/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CustomAccordion, CustomTooltip } from '@opengeoweb/shared';
import { ChevronDown, ChevronUp, LockLocked } from '@opengeoweb/theme';
import type { WorkspaceSupportedComponentTypes } from '../../store/viewPresets/types';
import { ALL_COMPONENT_TYPES } from '../../store/viewPresets/types';
import type {
  WorkspaceLabelsConfigResponse,
  WorkspacePresetLabels,
} from '../../store/workspace/types';
import type { WorkspacePresetFormValues } from '../../store/workspaceList/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { humanizeLabelStrings } from '../../utils/utils';

type GroupKey = string;
export const VIEW_TYPE_KEY: GroupKey = 'viewType';

interface PresetLabelsPanelProps {
  labelsConfig: WorkspaceLabelsConfigResponse;
  viewComponentType?: WorkspaceSupportedComponentTypes;
  onLabelsChange?: (labels: WorkspacePresetLabels) => void;
  menuLabels?: WorkspacePresetLabels;
  isMenuFilterMode?: boolean;
}

const normalizeLabels = (
  labels: WorkspacePresetLabels,
  available: WorkspaceLabelsConfigResponse,
): WorkspacePresetLabels => {
  const result: WorkspacePresetLabels = {};

  const allKeys = Array.from(
    new Set([
      ...Object.keys(available ?? {}),
      ...(labels ? Object.keys(labels) : []),
    ]),
  );

  allKeys.forEach((key) => {
    result[key] = labels?.[key] ?? [];
  });

  return result;
};

const PresetLabelsPanel: React.FC<PresetLabelsPanelProps> = ({
  labelsConfig,
  viewComponentType,
  onLabelsChange,
  menuLabels,
  isMenuFilterMode: isMenuFilterModeProp = false,
}) => {
  const isMenuFilterMode = Boolean(isMenuFilterModeProp);
  const { t } = useWorkspaceTranslation();
  const { control, setValue } = useFormContext<WorkspacePresetFormValues>();

  const watchedLabels = useWatch({
    control,
    name: 'labels',
  }) as WorkspacePresetLabels;

  const watchedViews = useWatch({
    control,
    name: 'views',
  }) as { componentType?: string }[] | undefined;

  const labels = React.useMemo(
    () => normalizeLabels(watchedLabels, labelsConfig),
    [watchedLabels, labelsConfig],
  );

  const groupKeys = React.useMemo(
    () =>
      Array.from(
        new Set([
          VIEW_TYPE_KEY, // ensure this group exists
          ...Object.keys(labelsConfig ?? {}),
          ...(watchedLabels ? Object.keys(watchedLabels) : []),
        ]),
      ),
    [labelsConfig, watchedLabels],
  );

  const orderedGroupKeys = React.useMemo(() => {
    const withoutViewType = groupKeys.filter((key) => key !== VIEW_TYPE_KEY);

    // If for some reason viewType really isn't in groupKeys, just return as-is
    if (!groupKeys.includes(VIEW_TYPE_KEY)) {
      return withoutViewType;
    }

    if (isMenuFilterMode) {
      // MENU MODE: viewType should be NEXT TO LAST.
      if (withoutViewType.length === 0) {
        return [VIEW_TYPE_KEY];
      }
      if (withoutViewType.length === 1) {
        // Only one other group → [viewType, other] or [other, viewType]?
        // "Next to last" with one element basically just means "first".
        return [VIEW_TYPE_KEY, withoutViewType[0]];
      }

      const last = withoutViewType[withoutViewType.length - 1];
      const rest = withoutViewType.slice(0, -1);
      return [...rest, VIEW_TYPE_KEY, last];
    }

    // FORM MODE: viewType should be LAST.
    return [...withoutViewType, VIEW_TYPE_KEY];
  }, [groupKeys, isMenuFilterMode]);

  const titleForGroup = React.useCallback(
    (key: GroupKey): string => {
      const translationKey = `workspace-labels-${key}`;
      const translated = t(translationKey as string);
      return translated !== translationKey
        ? translated
        : humanizeLabelStrings(key);
    },
    [t],
  );

  const labelFor = (group: GroupKey, value: string): string => {
    const translationKey = `workspace-labels-${group}-${value}`;
    const translated = t(translationKey as string);
    if (translated !== translationKey) {
      return translated;
    }
    return humanizeLabelStrings(value);
  };

  // viewType is actually based on componentType.
  // In workspace form mode, we derive it from the real views array.
  // In view preset form, we fall back to viewComponentType.
  const componentTypes = React.useMemo(() => {
    const viewsArray = Array.isArray(watchedViews) ? watchedViews : [];

    const fromViews = viewsArray
      .map((view) => view?.componentType)
      .filter((value): value is string => Boolean(value));

    if (fromViews.length > 0) {
      return Array.from(new Set(fromViews));
    }

    if (viewComponentType) {
      return [viewComponentType];
    }

    return [] as string[];
  }, [watchedViews, viewComponentType]);

  const viewTypeLabels = React.useMemo(
    () => (labels[VIEW_TYPE_KEY] ?? []) as string[],
    [labels],
  );
  const viewTypeConfigItems = React.useMemo(
    () =>
      ((labelsConfig?.[VIEW_TYPE_KEY] as string[] | undefined) ?? []).slice(),
    [labelsConfig],
  );

  const viewTypeItems: string[] = React.useMemo(() => {
    if (isMenuFilterMode) {
      if (viewTypeConfigItems.length > 0) {
        return viewTypeConfigItems;
      }
      return ALL_COMPONENT_TYPES.slice();
    }
    return componentTypes.length ? componentTypes : viewTypeLabels;
  }, [isMenuFilterMode, componentTypes, viewTypeLabels, viewTypeConfigItems]);

  React.useEffect(() => {
    if (!isMenuFilterMode) {
      return;
    }
    if (!menuLabels) {
      return;
    }

    setValue('labels', menuLabels, { shouldDirty: false });
  }, [isMenuFilterMode, menuLabels, setValue]);

  // In form mode, keep locked "viewType" labels in sync with componentTypes
  React.useEffect(() => {
    if (isMenuFilterMode) {
      return;
    }

    if (!componentTypes.length) {
      return;
    }

    const currentViewType = (labels[VIEW_TYPE_KEY] ?? []) as string[];

    const sameLength = currentViewType.length === componentTypes.length;
    const sameValues =
      sameLength &&
      currentViewType.every((val, idx) => val === componentTypes[idx]);

    if (sameValues) {
      return;
    }

    setValue(`labels.${VIEW_TYPE_KEY}`, componentTypes, {
      shouldDirty: true,
    });
  }, [isMenuFilterMode, componentTypes, labels, setValue]);

  const totalActive = React.useMemo(
    () => groupKeys.reduce((sum, key) => sum + (labels[key] ?? []).length, 0),
    [groupKeys, labels],
  );

  const activeNonViewType = Math.max(0, totalActive - viewTypeLabels.length);

  const isClearAllDisabled = isMenuFilterMode
    ? totalActive === 0 // Menu mode: disable only if nothing selected at all
    : activeNonViewType === 0;

  const clearAll = (): void => {
    const newLabels: WorkspacePresetLabels = { ...labels };

    groupKeys.forEach((key) => {
      if (isMenuFilterMode || key !== VIEW_TYPE_KEY) {
        // Menu mode: clear everything including viewType
        // Form mode: clear everything except viewType
        newLabels[key] = [];
      }
    });

    setValue('labels', newLabels, { shouldDirty: true });

    if (isMenuFilterMode && onLabelsChange) {
      onLabelsChange(newLabels);
    }
  };

  const toggle = (key: GroupKey, item: string, locked?: boolean): void => {
    if (locked) {
      return;
    }

    const currentGroup: string[] = (labels[key] ?? []) as string[];
    const nextGroup = currentGroup.includes(item)
      ? currentGroup.filter((label) => label !== item)
      : [...currentGroup, item];

    const newLabels: WorkspacePresetLabels = {
      ...labels,
      [key]: nextGroup,
    };

    setValue('labels', newLabels, {
      shouldDirty: true,
      shouldTouch: true,
    });

    if (isMenuFilterMode && onLabelsChange) {
      onLabelsChange(newLabels);
    }
  };

  const groups = React.useMemo(
    () =>
      orderedGroupKeys.map((key) => {
        const isViewType = key === VIEW_TYPE_KEY;

        const items = isViewType
          ? viewTypeItems
          : ((labelsConfig[key] as string[] | undefined) ?? []);

        const locked = !isMenuFilterMode && isViewType;

        return {
          key,
          title: titleForGroup(key),
          items,
          locked,
        };
      }),
    [
      orderedGroupKeys,
      labelsConfig,
      viewTypeItems,
      isMenuFilterMode,
      titleForGroup,
    ],
  );

  const [expandAll, setExpandAll] = React.useState(false);
  const [resetKey, setResetKey] = React.useState(0);
  const toggleAll = (): void => {
    setExpandAll((prev) => !prev);
    setResetKey((key) => key + 1);
  };

  return (
    <Box
      sx={{
        py: 1,
      }}
    >
      <Typography sx={{ px: 2, mb: 1, mt: 1 }} variant="subtitle1">
        {t('workspace-presets-form-labels-header')}
      </Typography>

      <Box sx={{ px: 2, display: 'grid', rowGap: 1, mb: 2 }}>
        <Button
          color="secondary"
          disabled={isClearAllDisabled}
          onClick={clearAll}
          size="small"
          sx={{
            justifySelf: 'start',
            letterSpacing: 0.2,
            boxShadow: 'none',
            textTransform: 'none',
          }}
          variant="contained"
        >
          {t('workspace-presets-form-labels-clearAll', {
            count: totalActive,
          })}
        </Button>

        <Typography sx={{ color: 'text.secondary' }} variant="caption">
          {t('workspace-presets-form-labels-info')}
        </Typography>

        <Button
          endIcon={expandAll ? <ChevronUp /> : <ChevronDown />}
          onClick={toggleAll}
          size="small"
          sx={{
            justifySelf: 'start',
            textTransform: 'none',
            minWidth: 120,
            maxWidth: 120,
          }}
          variant="outlined"
        >
          {expandAll
            ? t('workspace-presets-form-labels-collapseAll')
            : t('workspace-presets-form-labels-expandAll')}
        </Button>
      </Box>

      {/* Groups */}
      <Stack spacing={1} sx={{ px: 1 }}>
        {groups.map(({ key, title, items, locked }) => {
          const groupItems = items ?? [];
          const currentGroupLabels = (labels[key] ?? []) as string[];
          const active = currentGroupLabels.length;

          return (
            <CustomAccordion
              data-testid={`workspace-labels-drawer-${key}`}
              defaultExpanded={expandAll}
              key={`${resetKey}-${key}-${expandAll}`}
              sx={{
                border: 'none',
                width: '100%',
                boxShadow: 'none',
                '&::before': {
                  display: 'none',
                },
                '& .MuiPaper-root, & .MuiAccordion-root': {
                  boxShadow: 'none',
                },
                '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                  {
                    marginY: 0,
                  },
                '& .MuiAccordionDetails-root': {
                  paddingTop: 0.25,
                  paddingBottom: 0.75,
                },
                '& .MuiAccordionSummary-root.Mui-expanded': {
                  borderBottom: 'none',
                },
              }}
              title={
                <Stack
                  alignItems="center"
                  direction="row"
                  sx={{ flexGrow: 1, minWidth: 0 }}
                >
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Typography
                      sx={{ fontSize: '14px' }}
                    >{`${title} (${groupItems.length})`}</Typography>
                    {!!locked && <LockLocked fontSize="small" />}
                  </Stack>

                  {active > 0 && (
                    <Chip
                      color="primary"
                      label={`${active} ${t('workspace-label-active')}`}
                      size="small"
                      sx={{
                        ml: 'auto',
                        fontWeight: 500,
                        fontSize: 12,
                        border: 'none',
                        position: 'absolute',
                        right: 35,
                        cursor: 'default',
                        pointerEvents: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                        },
                      }}
                      variant="outlined"
                    />
                  )}
                </Stack>
              }
            >
              <Stack spacing={-0.8}>
                {groupItems.map((it) => {
                  const labelText = labelFor(key, it);

                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={currentGroupLabels.includes(it)}
                          onChange={() => toggle(key, it, locked)}
                          size="small"
                        />
                      }
                      disabled={locked}
                      key={it}
                      label={
                        <CustomTooltip
                          enterDelay={300}
                          placement="top"
                          title={labelText}
                        >
                          <Typography
                            noWrap
                            sx={{
                              fontSize: 16,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            variant="body2"
                          >
                            {labelText}
                          </Typography>
                        </CustomTooltip>
                      }
                      sx={{
                        width: '100%',
                        marginRight: 0,
                        '.MuiFormControlLabel-label': {
                          minWidth: 0,
                          flex: 1,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </CustomAccordion>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PresetLabelsPanel;
